import { EXPECTED_CHAIN_ID } from "@/configs/expected-chain";

type T_NET_MODE = "MAIN" | "FORK" | "TEST";
export const NET_MODE = (process.env.NEXT_PUBLIC_CONTRACT_NET ??
  "MAIN") as T_NET_MODE;

export const CHAIN_ID = EXPECTED_CHAIN_ID;

const isMainOrFork = NET_MODE === "MAIN" || NET_MODE === "FORK";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const address = (value: string | undefined) =>
  (value ?? ZERO_ADDRESS).toLowerCase() as `0x${string}`;

export const ContractAddress = {
  POLYGON_SCAN: isMainOrFork
    ? "https://polygonscan.com/tx/"
    : "https://amoy.polygonscan.com/tx/",
  POLYGON_ADDRESS_SCAN: isMainOrFork
    ? "https://polygonscan.com/address/"
    : "https://amoy.polygonscan.com/address/",

  MINER: address(process.env.NEXT_PUBLIC_NFT_MINER),
  MINER_MARKET: address(process.env.NEXT_PUBLIC_NFT_MINER_MARKET),

  DAO_SAFE_VAULT: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_DAO_SAFE_VAULT!
      : process.env.NEXT_PUBLIC_DAO_SAFE_VAULT!,
  ),

  STAKE: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_STAKE_META!
      : process.env.NEXT_PUBLIC_STAKE_META_TEST!,
  ),
  CALCULATOR: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_CALCULATOR!
      : process.env.NEXT_PUBLIC_CALCULATOR_TEST!,
  ),

  USDT: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_USDT!
      : process.env.NEXT_PUBLIC_USDT_TEST!,
  ),

  UVM: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_UVM!
      : process.env.NEXT_PUBLIC_UVM_TEST!,
  ),

  DNM: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_DNM!
      : process.env.NEXT_PUBLIC_DNM_TEST!,
  ),

  LANDS: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_NFT_LAND!
      : process.env.NEXT_PUBLIC_NFT_LAND_TEST!,
  ),

  GATEWAY: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_GATEWAY!
      : process.env.NEXT_PUBLIC_GATEWAY_TEST!,
  ),

  /**
   * Gateway V2 — deployed but not yet the app's gateway.
   *
   * `GATEWAY` above stays V1 (everything the site reads still lives there);
   * this address exists only so the DAO proposal form can target V2. Unlike
   * every sibling it tolerates a missing env instead of asserting non-null,
   * because addresses.ts is imported by nearly every page — a `!` here would
   * turn an unset var into a module-load TypeError that blanks the whole app
   * for any dev whose .env.local predates this change. Empty string instead,
   * and the DAO form drops the option (see CONTRACT_OPTIONS).
   */
  GATEWAY_V2: (process.env.NEXT_PUBLIC_GATEWAY_V2 ?? "").toLowerCase() as
    | `0x${string}`
    | "",

  /**
   * Gateway V3 — the transparent proxy, NOT the implementation. This is the
   * stable, user-facing address; the implementation behind it is replaceable
   * through GATEWAY_UPGRADE_CONTROLLER.
   *
   * Same missing-env tolerance as GATEWAY_V2 above, for the same reason.
   */
  GATEWAY_V3: (process.env.NEXT_PUBLIC_GATEWAY_V3 ?? "").toLowerCase() as
    | `0x${string}`
    | "",

  /**
   * GatewayUpgradeController — the two-party governance root for the Gateway V3
   * proxy. Not a business contract: the DAO's only interaction with it is
   * confirming a binding or upgrade proposal the developer has already made.
   *
   * Same missing-env tolerance as GATEWAY_V2 above, for the same reason.
   */
  GATEWAY_UPGRADE_CONTROLLER: (
    process.env.NEXT_PUBLIC_UPGRADE_CONTROLLER ?? ""
  ).toLowerCase() as `0x${string}` | "",

  LAZY_MINT: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_LAZY_MINT!
      : process.env.NEXT_PUBLIC_LAZY_MINT_TEST!,
  ),

  MARKET: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_MARKET!
      : process.env.NEXT_PUBLIC_MARKET_TEST!,
  ),

  LAND_MARKET: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_NFT_LAND_MARKET!
      : process.env.NEXT_PUBLIC_NFT_LAND_MARKET_TEST!,
  ),

  WRAPPER: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_WRAPPER!
      : process.env.NEXT_PUBLIC_WRAPPER_TEST!,
  ),

  UVM_BSC: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_UVM_BSC!
      : process.env.NEXT_PUBLIC_UVM_BSC_TEST!,
  ),

  DNM_BSC: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_DNM_BSC!
      : process.env.NEXT_PUBLIC_DNM_BSC_TEST!,
  ),

  BRIDGE_BSC: address(process.env.NEXT_PUBLIC_BRIDGE_BSC),

  LAND_BSC: address(process.env.NEXT_PUBLIC_NFTLAND_BSC),

  ELECTION: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_ELECTION!
      : process.env.NEXT_PUBLIC_ELECTION_TEST!,
  ),

  DAO: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_DAO!
      : process.env.NEXT_PUBLIC_DAO_TEST!,
  ),

  MARKET_NFT: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_MARKET_NFT!
      : process.env.NEXT_PUBLIC_MARKET_NFT_TEST!,
  ),

  NFT: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_NFT!
      : process.env.NEXT_PUBLIC_NFT_TEST!,
  ),

  LAUNCHPAD_FACTORY: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_LAUNCHPAD_FACTORY!
      : process.env.NEXT_PUBLIC_LAUNCHPAD_FACTORY_TEST!,
  ),
  LAUNCHPAD_MARKET: address(
    isMainOrFork
      ? process.env.NEXT_PUBLIC_LAUNCHPAD_MARKET!
      : process.env.NEXT_PUBLIC_LAUNCHPAD_MARKET_TEST!,
  ),
};

export default ContractAddress;
